import { useState, useEffect, useCallback, useRef } from 'react';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';
import { formatDate, aujourdHui } from '../../../../utils/jour';

const TYPE_LABELS = { annuel: 'Conge annuel', maladie: 'Maladie', permission: 'Permission', maternite: 'Maternite', paternite: 'Paternite', sans_solde: 'Sans solde' };
const TYPE_COLORS = { annuel: '#1565c0', maladie: '#e53935', permission: '#00897b', maternite: '#7b1fa2', paternite: '#7b1fa2', sans_solde: '#9e9e9e' };
const STATUT_LABELS = { en_attente: 'En attente', approuve: 'Approuve', refuse: 'Refuse' };
const STATUT_COLORS = { en_attente: '#f57c00', approuve: '#2e7d32', refuse: '#e53935' };
const JOURS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const ACCEPTED_TYPES = '.pdf,.jpg,.jpeg,.png,.docx';
const MAX_SIZE = 5 * 1024 * 1024;

const EMPTY = { agent_id: '', type: 'annuel', date_debut: '', date_fin: '', motif: '' };


function getMonthDays(year, month) {
  const days = [];
  const d = new Date(year, month, 1);
  while (d.getMonth() === month) { days.push(new Date(d)); d.setDate(d.getDate() + 1); }
  return days;
}

function diffDays(a, b) {
  if (!a || !b) return 0;
  return Math.ceil((new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24)) + 1;
}

export default function CongesPage() {
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [justifFile, setJustifFile] = useState(null);
  const fileInputRef = useRef(null);
  const [viewMonth, setViewMonth] = useState(() => { const n = new Date(); return { year: n.getFullYear(), month: n.getMonth() }; });

  useEscapeClose(showForm, () => setShowForm(false));

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [cRes, aRes] = await Promise.all([
        api.get('/conges').catch(() => ({ data: [] })),
        api.get('/agents').catch(() => ({ data: [] })),
      ]);
      setItems(cRes.data);
      setAgents(aRes.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY); setJustifFile(null); setError(''); setShowForm(true); };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_SIZE) { setError('Fichier trop volumineux (max 5 Mo)'); return; }
    setJustifFile(file);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const fd = new FormData();
      fd.append('agent_id', form.agent_id);
      fd.append('type', form.type);
      fd.append('date_debut', form.date_debut);
      fd.append('date_fin', form.date_fin);
      fd.append('motif', form.motif || '');
      if (justifFile) fd.append('justificatif', justifFile);
      await api.post('/conges', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setShowForm(false); setForm(EMPTY); setJustifFile(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); } finally { setSaving(false); }
  };

  const handleDecision = async (id, decision) => {
    try { await api.put(`/conges/${id}/decision`, { statut: decision }); load(); } catch {}
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const agentName = (id) => { const a = agents.find(x => x.id === id); return a ? `${a.prenom} ${a.nom}` : '-'; };

  const enAttente = items.filter(c => c.statut === 'en_attente').length;
  const enCours = items.filter(c => c.statut === 'approuve' && new Date(c.date_fin) >= new Date() && new Date(c.date_debut) <= new Date()).length;
  const totalApprouves = items.filter(c => c.statut === 'approuve').length;

  const statCards = [
    { label: 'Demandes en attente', value: enAttente, color: '#f57c00' },
    { label: 'Conges en cours', value: enCours, color: '#1565c0' },
    { label: 'Total approuves', value: totalApprouves, color: '#2e7d32' },
  ];

  const monthDays = getMonthDays(viewMonth.year, viewMonth.month);
  const monthLabel = new Date(viewMonth.year, viewMonth.month).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const prevMonth = () => setViewMonth(v => v.month === 0 ? { year: v.year - 1, month: 11 } : { ...v, month: v.month - 1 });
  const nextMonth = () => setViewMonth(v => v.month === 11 ? { year: v.year + 1, month: 0 } : { ...v, month: v.month + 1 });

  // Check if a day falls within an approved leave
  const dayHasLeave = (date) => {
    const ds = date.toISOString().split('T')[0];
    return items.some(c => c.statut === 'approuve' && ds >= c.date_debut?.split('T')[0] && ds <= c.date_fin?.split('T')[0]);
  };

  return (
    <div className="gss-page-scroll">
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {statCards.map(sc => (
          <div key={sc.label} className="content-card" style={{ padding: '1.2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: sc.color }}>{sc.value}</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--gss-text-light)', marginTop: '0.25rem' }}>{sc.label}</div>
          </div>
        ))}
      </div>

      <div className="gss-content-row">
        {/* Table */}
        <div className="content-card">
          <div className="gss-toolbar">
            <div className="content-card-title" style={{ margin: 0, border: 'none', padding: 0 }}>Demandes de conges</div>
            <button className="gss-btn gss-btn-primary" onClick={openCreate}>+ Nouvelle demande</button>
          </div>

          {loading ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
          ) : items.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDDDDD" strokeWidth="1.5" style={{ marginBottom: 16 }}>
                <path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
              </svg>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#666', marginBottom: 4 }}>Aucun enregistrement</div>
              <div style={{ fontSize: 14, color: '#999' }}>Cliquez sur "+ Nouvelle demande" pour commencer</div>
            </div>
          ) : (
            <div style={{ overflowX: 'scroll' }}>
              <table className="gss-table" style={{ minWidth: 900 }}>
                <thead><tr><th style={{ width: 150 }}>Agent</th><th style={{ width: 100 }}>Type</th><th style={{ width: 100 }}>Debut</th><th style={{ width: 100 }}>Fin</th><th style={{ width: 70 }}>Duree</th><th style={{ width: 60 }}>Doc</th><th style={{ width: 100 }}>Statut</th><th style={{ width: 180 }}>Actions</th></tr></thead>
                <tbody>
                  {items.map(c => (
                    <tr key={c.id}>
                      <td><strong>{agentName(c.agent_id)}</strong></td>
                      <td><span className="gss-badge" style={{ background: (TYPE_COLORS[c.type] || '#999') + '20', color: TYPE_COLORS[c.type] || '#999' }}>{TYPE_LABELS[c.type] || c.type}</span></td>
                      <td>{formatDate(c.date_debut)}</td>
                      <td>{formatDate(c.date_fin)}</td>
                      <td>{diffDays(c.date_debut, c.date_fin)} j</td>
                      <td style={{ textAlign: 'center' }}>
                        {c.justificatif_url ? (
                          <a href={`${c.justificatif_url}`} target="_blank" rel="noopener noreferrer" title="Voir justificatif" style={{ color: '#1565c0', fontSize: 18 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
                          </a>
                        ) : <span style={{ color: '#ccc' }}>—</span>}
                      </td>
                      <td><span className="gss-badge" style={{ background: (STATUT_COLORS[c.statut] || '#999') + '20', color: STATUT_COLORS[c.statut] || '#999' }}>{STATUT_LABELS[c.statut] || c.statut}</span></td>
                      <td>
                        {c.statut === 'en_attente' ? (
                          <div style={{ display: 'flex', gap: 8, whiteSpace: 'nowrap' }}>
                            <button style={{ padding: '4px 8px', fontSize: 12, borderRadius: 6, border: '1px solid #1A8754', background: 'rgba(26,135,84,0.06)', color: '#1A8754', cursor: 'pointer', fontWeight: 500 }} onClick={() => handleDecision(c.id, 'approuve')}>Approuver</button>
                            <button style={{ padding: '4px 8px', fontSize: 12, borderRadius: 6, border: '1px solid #CC0000', background: 'rgba(204,0,0,0.06)', color: '#CC0000', cursor: 'pointer', fontWeight: 500 }} onClick={() => handleDecision(c.id, 'refuse')}>Refuser</button>
                          </div>
                        ) : (
                          <span style={{ fontSize: '0.8rem', color: 'var(--gss-text-light)' }}>{c.approuve_par || '-'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Circuit validation info */}
          <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--gss-bg)', borderRadius: 8, fontSize: 12, color: 'var(--gss-text-light)' }}>
            <strong>Circuit de validation :</strong> Demande → Superviseur → RH → DG
          </div>
        </div>

        {/* Calendar */}
        <div className="content-card" style={{ height: 'fit-content' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <button className="gss-btn-sm" onClick={prevMonth}>&larr;</button>
            <span style={{ fontWeight: 600, textTransform: 'capitalize', fontSize: 13 }}>{monthLabel}</span>
            <button className="gss-btn-sm" onClick={nextMonth}>&rarr;</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, textAlign: 'center', fontSize: 11 }}>
            {JOURS.map(j => <div key={j} style={{ fontWeight: 600, color: 'var(--gss-text-light)', padding: 3 }}>{j}</div>)}
            {Array.from({ length: monthDays[0]?.getDay() || 0 }).map((_, i) => <div key={`e-${i}`} />)}
            {monthDays.map(d => {
              const hasLeave = dayHasLeave(d);
              const isToday = d.toISOString().split('T')[0] === aujourdHui();
              return (
                <div key={d.toISOString()}
                  style={{
                    padding: '5px 0', borderRadius: 4,
                    background: hasLeave ? 'rgba(21,101,192,0.12)' : isToday ? 'rgba(139,26,26,0.08)' : 'transparent',
                    color: hasLeave ? '#1565c0' : isToday ? '#8B1A1A' : 'var(--gss-text)',
                    fontWeight: hasLeave || isToday ? 700 : 400,
                    fontSize: 11,
                  }}>
                  {d.getDate()}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 10, fontSize: 10, color: 'var(--gss-text-light)', display: 'flex', gap: 12 }}>
            <span><i style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: 'rgba(21,101,192,0.3)', marginRight: 4, verticalAlign: 'middle' }} />Conge approuve</span>
            <span><i style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: 'rgba(139,26,26,0.2)', marginRight: 4, verticalAlign: 'middle' }} />Aujourd'hui</span>
          </div>
        </div>
      </div>

      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                <h2>Nouvelle demande de conge</h2>
              </div>
              <div className="gss-modal-subtitle">Gestion des conges et absences</div>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            {error && <div style={{ padding: '0.5rem 1.5rem', color: '#e53935', fontSize: '0.9rem' }}>{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="gss-form-grid" style={{ padding: '1.5rem' }}>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  <span>IDENTIFICATION</span>
                </div>

                <div className="gss-field gss-field-full">
                  <label>Agent *</label>
                  <select className="gss-input" required value={form.agent_id} onChange={e => setField('agent_id', e.target.value)}>
                    <option value="">-- Selectionner --</option>
                    {agents.filter(a => a.statut === 'actif').map(a => (
                      <option key={a.id} value={a.id}>{a.matricule} — {a.prenom} {a.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="gss-field gss-field-full">
                  <label>Type de conge *</label>
                  <select className="gss-input" required value={form.type} onChange={e => setField('type', e.target.value)}>
                    {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  <span>PERIODE & JUSTIFICATION</span>
                </div>

                {/* Ligne 1 : Date debut + Date fin */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gss-text-light)', display: 'block', marginBottom: 4 }}>Date debut *</label>
                    <input className="gss-input" type="date" required value={form.date_debut} onChange={e => setField('date_debut', e.target.value)} style={{ width: '100%' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gss-text-light)', display: 'block', marginBottom: 4 }}>Date fin *</label>
                    <input className="gss-input" type="date" required value={form.date_fin} onChange={e => setField('date_fin', e.target.value)} style={{ width: '100%' }} />
                  </div>
                </div>

                {/* Ligne 2 : Periode + Justificatif */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gss-text-light)', display: 'block', marginBottom: 4 }}>Periode</label>
                    <div style={{ fontSize: 13, color: 'var(--gss-text-secondary)', padding: '10px 12px', background: 'var(--gss-bg)', borderRadius: 6, minHeight: 40, display: 'flex', alignItems: 'center' }}>
                      {form.date_debut && form.date_fin
                        ? <span>Duree : <strong>{diffDays(form.date_debut, form.date_fin)} jour(s)</strong></span>
                        : <span style={{ color: '#bbb' }}>Selectionnez les dates</span>}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gss-text-light)', display: 'block', marginBottom: 4 }}>Justificatif</label>
                    <input type="file" ref={fileInputRef} accept={ACCEPTED_TYPES} onChange={handleFileSelect} style={{ display: 'none' }} />
                    {!justifFile ? (
                      <button type="button" onClick={() => fileInputRef.current?.click()} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
                        background: '#F9FAFB', border: '1px dashed #D1D5DB', borderRadius: 6,
                        cursor: 'pointer', fontSize: 13, color: '#555', width: '100%', minHeight: 40,
                      }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
                        Joindre fichier
                      </button>
                    ) : (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                        background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 6, fontSize: 12, minHeight: 40,
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1565c0" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
                        <span style={{ flex: 1, fontWeight: 500, color: '#1565c0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{justifFile.name}</span>
                        <button type="button" onClick={() => { setJustifFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} style={{
                          background: 'none', border: 'none', cursor: 'pointer', color: '#e53935', fontSize: 16, fontWeight: 700, lineHeight: 1, padding: '0 2px',
                        }}>&times;</button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Ligne 3 : Motif pleine largeur */}
                <div style={{ marginTop: 16 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gss-text-light)', display: 'block', marginBottom: 4 }}>Motif</label>
                  <textarea className="gss-input" rows="3" value={form.motif} onChange={e => setField('motif', e.target.value)} style={{ resize: 'vertical', width: '100%' }} placeholder="Raison de la demande..." />
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>
                  {saving ? 'Envoi...' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4z"/></svg> Soumettre la demande</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
